import type { KeyboardEvent, ReactNode, Ref } from 'react';

export interface RedGreenToggleProps {
  value: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  offLabel?: ReactNode;
  onLabel?: ReactNode;
  ariaLabel: string;
  id?: string;
  size?: 'small' | 'normal';
  /** Forwarded to the pill button, e.g. to focus the toggle after a blocked action. */
  ref?: Ref<HTMLButtonElement>;
  /** Forwarded to the pill button, for test targeting. */
  'data-testid'?: string;
}

export const RedGreenToggle = ({
  value,
  onChange,
  disabled,
  offLabel,
  onLabel,
  ariaLabel,
  id,
  size,
  ref,
  'data-testid': dataTestId,
}: RedGreenToggleProps) => {
  const handleClick = () => {
    if (disabled) return;
    onChange(!value);
  };

  const handleKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onChange(!value);
    }
  };

  return (
    <span className={`rg-toggle rg-toggle--${size ?? 'normal'}`}>
      {offLabel != null && <span className="rg-toggle__off-label">{offLabel}</span>}
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={ariaLabel}
        id={id}
        disabled={disabled}
        data-testid={dataTestId}
        className={`rg-toggle__pill${value ? ' is-on' : ' is-off'}${disabled ? ' is-disabled' : ''}`}
        onClick={handleClick}
        onKeyDown={handleKey}
      >
        <span className="rg-toggle__knob" />
      </button>
      {onLabel != null && <span className="rg-toggle__on-label">{onLabel}</span>}
    </span>
  );
};
